// Loose item entities (E-17; PLR pickups). PURE (ENG-1).
//
// A minimal entity system that predates the M5 entity core: dropped ItemStacks
// fall, settle on the ground, merge with nearby same-item drops (radius 0.6),
// drift toward a nearby player (magnet), get collected within ~1 block, and
// despawn after five minutes. The client renders these as small billboards; the
// server (v1.1) runs the identical system.
import { ITEMDROP } from '../config';
import { type ItemStack, canStack, isEmpty } from '../game/inventory';

export interface ItemDrop {
  readonly id: number;
  stack: ItemStack;
  x: number; y: number; z: number;
  vx: number; vy: number; vz: number;
  age: number;
  pickupDelay: number;
}

/** Just enough world for item physics (ground rest). */
export interface DropWorld {
  isSolid(x: number, y: number, z: number): boolean;
  /** Horizontal flow direction of the fluid at (x,y,z), or null if none (E-10).
   *  Optional so tests/hosts without fluids need not implement it. */
  flowAt?(x: number, y: number, z: number): { x: number; z: number } | null;
}

export interface SpawnOpts {
  vx?: number; vy?: number; vz?: number;
  /** Ticks before the drop is collectable (default PICKUP_DELAY). */
  pickupDelay?: number;
}

/**
 * A hopper's item-pickup column (RED-2): a drop is vacuumed when its centre lies
 * in the hopper's own cell or the cell directly above it. Hoppers ignore the
 * pickup delay that gates player collection. (The exact Java suck-box is a little
 * taller than a block; this cell model is the representative slice.)
 */
export function inHopperColumn(hx: number, hy: number, hz: number, dx: number, dy: number, dz: number): boolean {
  if (Math.floor(dx) !== hx || Math.floor(dz) !== hz) return false;
  const fy = Math.floor(dy);
  return fy === hy || fy === hy + 1;
}

export class ItemDropSystem {
  readonly drops: ItemDrop[] = [];
  private nextId = 1;

  constructor(private readonly maxStackOf: (item: number) => number) {}

  get count(): number { return this.drops.length; }

  spawn(stack: ItemStack, x: number, y: number, z: number, opts: SpawnOpts = {}): ItemDrop {
    const drop: ItemDrop = {
      id: this.nextId++,
      stack: { ...stack },
      x, y, z,
      vx: opts.vx ?? 0, vy: opts.vy ?? 0, vz: opts.vz ?? 0,
      age: 0,
      pickupDelay: opts.pickupDelay ?? ITEMDROP.PICKUP_DELAY,
    };
    this.drops.push(drop);
    return drop;
  }

  clear(): void { this.drops.length = 0; }

  /** Advance every drop one tick: gravity + ground rest, merge, despawn. */
  tick(world: DropWorld): void {
    for (let i = this.drops.length - 1; i >= 0; i--) {
      const d = this.drops[i]!;
      if (++d.age >= ITEMDROP.DESPAWN_TICKS) { this.drops.splice(i, 1); continue; }
      if (d.pickupDelay > 0) d.pickupDelay--;

      d.vy -= ITEMDROP.GRAVITY;
      d.vx *= ITEMDROP.DRAG; d.vz *= ITEMDROP.DRAG;
      // Flowing water/lava shoves the drop downstream (E-10). Applied before the
      // positional integrate so a waterfall visibly carries the item along.
      const flow = world.flowAt?.(Math.floor(d.x), Math.floor(d.y + 0.1), Math.floor(d.z));
      if (flow) { d.vx += flow.x * ITEMDROP.FLUID_PUSH; d.vz += flow.z * ITEMDROP.FLUID_PUSH; }
      d.x += d.vx; d.z += d.vz;
      const ny = d.y + d.vy;
      // Rest on the block below (the item's centre floats ~0.25 above the floor).
      if (d.vy < 0 && world.isSolid(Math.floor(d.x), Math.floor(ny - 0.2), Math.floor(d.z))) {
        d.y = Math.floor(ny - 0.2) + 1.05;
        d.vy = 0;
      } else {
        d.y = ny;
      }
    }
    this.mergeNearby();
  }

  private mergeNearby(): void {
    const r2 = ITEMDROP.MERGE_RADIUS * ITEMDROP.MERGE_RADIUS;
    for (let i = 0; i < this.drops.length; i++) {
      const a = this.drops[i]!;
      for (let j = i + 1; j < this.drops.length; j++) {
        const b = this.drops[j]!;
        if (!canStack(a.stack, b.stack)) continue;
        const dx = a.x - b.x, dy = a.y - b.y, dz = a.z - b.z;
        if (dx * dx + dy * dy + dz * dz > r2) continue;
        const cap = this.maxStackOf(a.stack.item);
        const room = cap - a.stack.count;
        if (room <= 0) continue;
        const move = Math.min(room, b.stack.count);
        a.stack = { ...a.stack, count: a.stack.count + move };
        b.stack = { ...b.stack, count: b.stack.count - move };
        if (b.stack.count <= 0) { this.drops.splice(j, 1); j--; }
      }
    }
  }

  /**
   * Vacuum drops in the hopper column at (hx,hy,hz) into that hopper. `onInsert`
   * deposits a stack into the hopper's slots and returns whatever didn't fit
   * (kept on the ground). Returns the number of items collected. Runs every tick,
   * independent of the hopper's transfer cooldown (RED-2).
   */
  vacuumInto(hx: number, hy: number, hz: number, onInsert: (stack: ItemStack) => ItemStack): number {
    let collected = 0;
    for (let i = this.drops.length - 1; i >= 0; i--) {
      const d = this.drops[i]!;
      if (!inHopperColumn(hx, hy, hz, d.x, d.y, d.z)) continue;
      const before = d.stack.count;
      const leftover = onInsert(d.stack);
      collected += before - (isEmpty(leftover) ? 0 : leftover.count);
      if (isEmpty(leftover)) this.drops.splice(i, 1);
      else d.stack = leftover;
    }
    return collected;
  }

  /**
   * Pull collectable drops toward (px,py,pz) and hand them off. `onPickup` inserts
   * into the inventory and returns whatever didn't fit (kept on the ground).
   * Returns the stacks actually collected (for pickup events / XP later).
   */
  collect(px: number, py: number, pz: number, onPickup: (stack: ItemStack) => ItemStack): ItemStack[] {
    const collected: ItemStack[] = [];
    const pick2 = ITEMDROP.PICKUP_RADIUS * ITEMDROP.PICKUP_RADIUS;
    const mag2 = ITEMDROP.MAGNET_RADIUS * ITEMDROP.MAGNET_RADIUS;
    for (let i = this.drops.length - 1; i >= 0; i--) {
      const d = this.drops[i]!;
      const dx = px - d.x, dy = py + 1 - d.y, dz = pz - d.z; // aim at the player's chest
      const dist2 = dx * dx + dy * dy + dz * dz;
      if (d.pickupDelay === 0 && dist2 <= pick2) {
        const leftover = onPickup(d.stack);
        if (isEmpty(leftover)) { collected.push(d.stack); this.drops.splice(i, 1); }
        else { collected.push({ ...d.stack, count: d.stack.count - leftover.count }); d.stack = leftover; }
        continue;
      }
      if (dist2 <= mag2) {
        const dist = Math.sqrt(dist2) || 1;
        d.vx += (dx / dist) * ITEMDROP.MAGNET_ACCEL;
        d.vy += (dy / dist) * ITEMDROP.MAGNET_ACCEL;
        d.vz += (dz / dist) * ITEMDROP.MAGNET_ACCEL;
      }
    }
    return collected;
  }
}
